import type { Verdict } from "@larder/shared";
import { useState } from "react";

/**
 * The one question worth interrupting for.
 *
 * It appears only when the house has genuinely run out of something — the gate
 * is `shouldOfferRebuy` in shared/, not a rule spelled out here — because a
 * prompt after every mouthful would be a tax on the core loop, while a prompt at
 * the moment you run out is the app knowing something the user doesn't yet.
 *
 * TWO steps, not one. "How was it?" and "buy it again?" are different questions
 * and the first is not a means to the second: the verdict is kept for its own
 * sake (ADR 0014), and collapsing them into "loved it — add to cart" would make
 * every stored opinion a side effect of a shopping decision.
 *
 * Dismissible everywhere, and dismissal costs nothing — the cart can also be
 * reached from a search miss and from any item.
 */
export function RebuyPrompt({
  name,
  onVerdict,
  onAddToCart,
  onClose,
  busy,
}: {
  name: string;
  /** Recorded immediately, whatever they do next. */
  onVerdict: (verdict: Verdict) => void;
  onAddToCart: () => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [asked, setAsked] = useState(false);

  const answer = (verdict: Verdict) => {
    onVerdict(verdict);
    // "Never again" is an answer to both questions at once. Offering to buy
    // something they have just written off would read as not listening.
    if (verdict === "never_again") onClose();
    else setAsked(true);
  };

  return (
    <>
      <div className="fabscrim" aria-hidden="true" onClick={onClose} />
      <div
        className="rebuy"
        role="dialog"
        aria-modal="true"
        aria-label={`That was the last ${name}`}
        data-testid="rebuy"
      >
        <button
          type="button"
          className="rebuy-close"
          onClick={onClose}
          aria-label="Not now"
          data-testid="rebuy-close"
        >
          ✕
        </button>

        {asked ? (
          <>
            <p className="rebuy-title">
              Put <strong>{name}</strong> on the list?
            </p>
            <p className="note">You've run out. This is the moment you forget.</p>
            <div className="rebuy-actions two">
              <button
                type="button"
                className="answer good"
                disabled={busy}
                onClick={onAddToCart}
                data-testid="rebuy-add"
              >
                Add to list
              </button>
              <button
                type="button"
                className="answer neutral"
                disabled={busy}
                onClick={onClose}
                data-testid="rebuy-skip"
              >
                No need
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="rebuy-title">
              That was the last of your <strong>{name}</strong>.
            </p>
            <p className="note">How was it?</p>
            <div className="rebuy-actions">
              <button
                type="button"
                className="answer good"
                disabled={busy}
                onClick={() => answer("loved")}
                data-testid="verdict-loved"
              >
                Loved it
              </button>
              <button
                type="button"
                className="answer neutral"
                disabled={busy}
                onClick={() => answer("fine")}
                data-testid="verdict-fine"
              >
                It was fine
              </button>
              <button
                type="button"
                className="answer bad"
                disabled={busy}
                onClick={() => answer("never_again")}
                data-testid="verdict-never"
              >
                Never again
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
